import { useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { UserProvider } from "@/utils/userContext/UserContext";
import { createBrowserRouter, RouterProvider, RouteObject } from "react-router-dom";
import {
  Form,
  Pricing,
  Contract,
  SigninPayment,
  SigninForm,
  LoginSelect,
} from "@/components/index";
import Clientes from "@/pages/Dashboard/Clientes/index";
import DashboardHome from "@/pages/Dashboard/DashboardHome/index";
import MeuCoworking from "@/pages/Dashboard/MeuCoworking/index";
import MeuPerfil from "@/pages/Dashboard/MeuPerfil/index";
import Login from "@/pages/Login/Login";
import Signin from "@/pages/Signin/Signin";
import Dashboard from "@/pages/Dashboard/index";
import LandingPage from "@/pages/LandingPage";
import ProtectedRoute from "@/utils/auth/ProtectedRoute";
import RoleProtectedRoute from "@/utils/auth/RoleProtectedRoute";
import RouteWatcher from "@/routes/RouteWatcher";
import Info from "@/pages/LandingPage/Info/Info.tsx";
import Notificacoes from "@/pages/Dashboard/Notificacoes";
import Produtos from "@/pages/Dashboard/Produtos";
import Inscricoes from "@/pages/Dashboard/Inscricoes/SubscriptionsList";
import Agenda from "@/pages/Dashboard/Agenda";
import Afiliados from "@/pages/Dashboard/Afiliados";
import AuthExpiredListener from "@/components/AuthExpiredListener/AuthExpiredListener";
import { ToastContainer } from "react-toastify";
import GlobalModalProvider from "@/components/GlobalModalProvider/GlobalModalProvider";
import ResetPassword from "@/components/ResetPassword/ResetPassword";
import DataInconsistencyCheck from "@/components/DataInconsistencyCheck/DataInconsistencyCheck";

// Configure QueryClient with sensible caching defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Cache kept for 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      retry: 1, // Retry failed requests once
    },
  },
});

const Router = () => {
  const homeRef = useRef<HTMLElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (id: string) => {
    const sectionRef = { home: homeRef, info: infoRef, contact: contactRef }[id];
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Simplified RouteWatcher wrapper
  const WithRouteWatcher: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
      {children}
      <RouteWatcher onRouteChange={() => {}} />
    </>
  );

  const routes: RouteObject[] = [
    {
      path: "/login",
      element: <WithRouteWatcher><Login /></WithRouteWatcher>,
    },
    {
      path: "/login-select",
      element: <WithRouteWatcher><LoginSelect /></WithRouteWatcher>,
    },{
      path: "/reset-password",
      element: <WithRouteWatcher> <ResetPassword/> </WithRouteWatcher>
    },
    {
      path: "/signin",
      element: <WithRouteWatcher><Signin /></WithRouteWatcher>,
      children: [
        {
          path: "signin-form",
          element: <SigninForm />,
        },
        {
          path: "signin-payment",
          element: <SigninPayment />,
        },
        {
          path: "contract",
          element: <Contract />,
        },
      ],
    },
    {
      path: "/dashboard",
      element: (
        <WithRouteWatcher>
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        </WithRouteWatcher>
      ),
      children: [
        {
          path: "",
          element: (
            <RoleProtectedRoute allowedRoles={["OWNER"]} redirectTo="/dashboard/clientes">
              <DashboardHome />
            </RoleProtectedRoute>
          ),
        },
        {
          path: "clientes",
          element: <Clientes />,
        },
        {
          path: "minha-empresa",
          element: <MeuCoworking />,
        },
        {
          path: "meu-perfil",
          element: <MeuPerfil />,
        },
        {
          path:"notificacoes",
          element:<Notificacoes/>,
        },
        {
          path:"produtos",
          element:<Produtos/>,
        },
        {
          path:"inscricoes",
          element:<Inscricoes/>,
        },
        {
          path:"afiliados",
          element:<Afiliados/>,
        },
        {
          path:"agenda",
          element:<Agenda/>,
        }
      ],
    },
    {
      path: "/",
      element: <WithRouteWatcher><LandingPage /></WithRouteWatcher>,
      children: [
        {
          path: "info",
          element: <Info scrollToSection={scrollToSection} />,
        },
        {
          path: "pricing",
          element: <Pricing scrollToSection={scrollToSection} />,
        },
        {
          path: "form",
          element: <Form />,
        },
      ],
    },
  ];

  const router = createBrowserRouter(routes);

  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <GlobalModalProvider>
          <ToastContainer />
          <AuthExpiredListener />
          <DataInconsistencyCheck />
          <RouterProvider router={router} />
        </GlobalModalProvider>
      </UserProvider>
    </QueryClientProvider>
  );
};

export default Router;
